import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Calendar, Package, Vote, CalendarClock, Receipt, Users } from "lucide-react";
import { Button, Card, CardContent, SegmentedControl, cn } from "@tabipla/ui";
import { ItineraryDayNavigator } from "../itinerary";
import { EmptyState } from "../EmptyState";
import { PackingList } from "../PackingList";
import { MapView } from "../maps";
import { VotingPanel } from "../VotingPanel";
import { SchedulePanel } from "../SchedulePanel";
import { TripMembersSection } from "../TripMembersSection";
import { NextSpotCard, SettlementBanner, ViewModeToggle } from "./index";
import type { TripDetailContentProps } from "./types";

// Lazy load chat panel (includes react-markdown)
const TripChatPanel = lazy(() =>
  import("./TripChatPanel").then((m) => ({ default: m.TripChatPanel }))
);

export function TripDetailContent({
  trip,
  tripId,
  groupMembers,
  currentGroup,
  tripMode,
  activeTab,
  viewMode,
  todaysItinerary,
  nextSpot,
  chat,
  onTabChange,
  onViewModeChange,
  onAddSpot,
  onEditSpot,
  onInviteClick,
  onGroupSelect,
  onQuickExpense,
}: TripDetailContentProps) {
  const { t } = useTranslation();

  const hasItinerary = trip.itineraries && trip.itineraries.length > 0;

  return (
    <>
      {/* Group Section */}
      <Card className="overflow-hidden">
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-full",
                  currentGroup?.iconColor
                    ? `bg-gradient-to-br ${currentGroup.iconColor}`
                    : "bg-primary/10"
                )}
              >
                {currentGroup?.iconEmoji || <Users className="h-4 w-4 text-primary" />}
              </div>
              <div className="min-w-0">
                {trip.groupId && currentGroup ? (
                  <>
                    <p className="truncate font-medium">{currentGroup.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {currentGroup.memberCount} {t("groups.members", "メンバー")}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t("trips.noGroup", "グループ未設定")}
                  </p>
                )}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={onGroupSelect}>
              {trip.groupId
                ? t("trips.changeGroup", "変更")
                : t("trips.addToGroup", "グループに追加")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Members Section */}
      {trip.groupId && groupMembers && (
        <TripMembersSection members={groupMembers} onInviteClick={onInviteClick} />
      )}

      {/* Tab Navigation */}
      <SegmentedControl
        value={activeTab}
        onChange={onTabChange}
        size="sm"
        options={[
          {
            value: "itinerary",
            label: t("trips.detailShort"),
            icon: <Calendar className="h-3.5 w-3.5" />,
          },
          {
            value: "packing",
            label: t("packing.titleShort"),
            icon: <Package className="h-3.5 w-3.5" />,
          },
          {
            value: "polls",
            label: t("polls.titleShort"),
            icon: <Vote className="h-3.5 w-3.5" />,
          },
          {
            value: "schedules",
            label: t("schedules.titleShort"),
            icon: <CalendarClock className="h-3.5 w-3.5" />,
          },
        ]}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content Area */}
        <div className={chat.showChat ? "lg:col-span-2" : "lg:col-span-3"}>
          {activeTab === "itinerary" ? (
            <>
              {!hasItinerary ? (
                <Card>
                  <CardContent className="py-12">
                    <EmptyState
                      icon={Sparkles}
                      title={t("trips.noItinerary")}
                      description={t("trips.noItineraryDescription", "AIが旅程を提案します")}
                    />
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {/* Next Spot Card - Show in traveling mode */}
                  {tripMode === "traveling" && todaysItinerary && (
                    <NextSpotCard nextSpot={nextSpot} />
                  )}

                  {/* Settlement Banner - Show in settlement mode */}
                  {tripMode === "settlement" && trip.groupId && (
                    <SettlementBanner groupId={trip.groupId} />
                  )}

                  {/* View Mode Toggle */}
                  <ViewModeToggle value={viewMode} onChange={onViewModeChange} />

                  {viewMode === "list" || viewMode === "timeline" ? (
                    <ItineraryDayNavigator
                      tripId={tripId}
                      itineraries={trip.itineraries}
                      viewMode={viewMode}
                      onAddSpot={onAddSpot}
                      onEditSpot={onEditSpot}
                    />
                  ) : (
                    <MapView
                      itineraries={trip.itineraries.sort((a, b) => a.dayNumber - b.dayNumber)}
                      destination={trip.destination}
                      onSpotClick={onEditSpot}
                    />
                  )}
                </div>
              )}
            </>
          ) : activeTab === "packing" ? (
            <PackingList tripId={tripId} />
          ) : activeTab === "polls" ? (
            <VotingPanel tripId={tripId} />
          ) : (
            <SchedulePanel tripId={tripId} />
          )}
        </div>

        {/* Chat Panel */}
        <Suspense fallback={null}>
          <TripChatPanel
            tripTitle={trip.title}
            showChat={chat.showChat}
            isChatFullScreen={chat.isChatFullScreen}
            chatInput={chat.chatInput}
            currentSessionId={chat.currentSessionId}
            showSessionDropdown={chat.showSessionDropdown}
            isSending={chat.isSending}
            messageActions={chat.messageActions}
            sessions={chat.sessions}
            chatMessages={chat.chatMessages}
            messagesEndRef={chat.messagesEndRef}
            isPendingSend={chat.isSending}
            onSetShowChat={chat.setShowChat}
            onSetIsChatFullScreen={chat.setIsChatFullScreen}
            onSetChatInput={chat.setChatInput}
            onSetShowSessionDropdown={chat.setShowSessionDropdown}
            onNewSession={chat.handleNewSession}
            onSelectSession={chat.handleSelectSession}
            onDeleteSession={chat.handleDeleteSession}
            onSendMessage={chat.handleSendMessage}
            onCloseChat={chat.closeChat}
          />
        </Suspense>
      </div>

      {/* Quick Expense Button - Traveling Mode */}
      {tripMode === "traveling" && trip.groupId && (
        <button
          onClick={onQuickExpense}
          className="fixed bottom-24 right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-green-500 text-white shadow-lg transition-all hover:bg-green-600 hover:shadow-xl active:scale-95 lg:bottom-8"
          title={t("trips.quickExpense.title")}
          aria-label={t("trips.quickExpense.title")}
        >
          <Receipt className="h-6 w-6" />
        </button>
      )}
    </>
  );
}
